import { z } from 'zod'

export function selectionValues(value) {
  const values = Array.isArray(value) ? value : value == null ? [] : [value]
  return [
    ...new Set(
      values
        .flatMap((entry) => String(entry).split(','))
        .map((entry) => entry.trim())
        .filter(
          (entry) => entry && !['any', 'all'].includes(entry.toLowerCase()),
        ),
    ),
  ]
}

export function pickerOptions(
  options = [],
  {
    valueKey = 'name',
    labelKey = 'nicename',
    days = false,
    games = false,
  } = {},
) {
  const seen = new Set()
  return options.flatMap((option) => {
    const value = String(
      typeof option === 'string'
        ? option
        : (option[valueKey] ?? option.value ?? option.id ?? ''),
    )
    if (!selectionValues(value).length || seen.has(value)) return []
    seen.add(value)
    const label =
      typeof option === 'string'
        ? option
        : option.longname ||
          option[labelKey] ||
          option.label ||
          option.name ||
          value
    const shortLabel = days
      ? label.slice(0, 3)
      : typeof option === 'object'
        ? option.nicename || label
        : label
    return [
      {
        value,
        label,
        shortLabel,
        keywords: [
          value,
          label,
          shortLabel,
          option.abbreviation || '',
          ...(option.keywords || []),
        ],
        group: games ? (option.featured ? 'Popular' : 'All games') : undefined,
      },
    ]
  })
}

export function filterPickerOptions(options, query) {
  const words = query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean)
  return options.filter((option) => {
    const text = [
      option.value,
      option.label,
      option.shortLabel,
      ...(option.keywords || []),
    ]
      .join(' ')
      .toLocaleLowerCase()
    return words.every((word) => text.includes(word))
  })
}

export function selectionSummary(options, value) {
  const values = selectionValues(value)
  const names = values.map(
    (v) =>
      options.find((option) => option.value === v)?.shortLabel ||
      options.find((option) => option.value === v)?.label ||
      v,
  )
  if (!names.length) return 'Any'
  if (names.length <= 2 && names.join(', ').length <= 18)
    return names.join(', ')
  return names[0] + (names.length > 1 ? ` +${names.length - 1}` : '')
}

export function normaliseDraft(fields, source = {}) {
  return Object.fromEntries(
    fields.map((field) => {
      const raw = source[field.key] ?? field.defaultValue
      return [
        field.key,
        field.kind === 'text'
          ? String(raw || '')
          : field.multi
            ? selectionValues(raw)
            : selectionValues(raw)[0] || field.defaultValue || null,
      ]
    }),
  )
}

export function draftSignature(value) {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, entry]) => [
          key,
          Array.isArray(entry) ? [...entry].sort() : entry,
        ]),
    ),
  )
}

export function draftSchema(fields) {
  return z.object(
    Object.fromEntries(
      fields.map((field) => {
        if (field.kind === 'text')
          return [
            field.key,
            field.maxLength ? z.string().max(field.maxLength) : z.string(),
          ]
        const optionValues = field.options.map((option) => option.value)
        const choice = z
          .string()
          .refine(
            (value) => optionValues.includes(value),
            `Choose a valid ${field.label.toLowerCase()}`,
          )
        return [field.key, field.multi ? z.array(choice) : choice.nullable()]
      }),
    ),
  )
}

// Availability changes with the draft; existing selections are never rewritten.
export function compatibleFilterFields(
  fields,
  draft,
  games,
  platformKey = 'platforms',
) {
  const selectedGame = games.find((game) => game.name === draft.game)
  const platformsFor = (game) =>
    game?.platforms?.map(
      (entry) => entry.platformName || entry.platform?.name || entry.name,
    )
  const platforms = selectionValues(draft[platformKey])
  const carFields = fields.filter((field) => field.gameCars)
  const matchesCars = (game) =>
    carFields.every((field) =>
      selectionValues(draft[field.key]).every((value) =>
        game.carClasses?.some((car) => car.name === value),
      ),
    )
  return fields.map((field) => {
    const selected = selectionValues(draft[field.key])
    return {
      ...field,
      disabled:
        field.disabled || (field.gameCars && !selectedGame && !selected.length),
      options: field.options?.map((option) => {
        let compatible = true
        if (field.key === 'game') {
          const game = games.find((entry) => entry.name === option.value)
          const supported = platformsFor(game)
          compatible =
            !game ||
            ((!supported ||
              platforms.every((value) => supported.includes(value))) &&
              matchesCars(game))
        } else if (field.key === platformKey && selectedGame) {
          const supported = platformsFor(selectedGame)
          compatible = !supported || supported.includes(option.value)
        } else if (field.gameCars) {
          compatible = Boolean(
            selectedGame?.carClasses?.some((car) => car.name === option.value),
          )
        }
        return {
          ...option,
          disabled:
            option.disabled ||
            (!selected.includes(option.value) && !compatible),
        }
      }),
    }
  })
}
